import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Label } from '@/components/ui-kit/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { useToast } from '@/hooks/use-toast';
import {
  useGetDepartmentsLookup,
  useGetEmployeesLookup,
  useTransferToConditionalOffer,
} from '../../hooks/use-profiles';
import {
  DepartmentOption,
  EmployeesLookupResult,
  InterviewPanelMember,
  PotentialProfile,
} from '../../types/profile.types';

export interface TransferDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: PotentialProfile | null;
}

export const TransferDialog = ({ open, onOpenChange, profile }: TransferDialogProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [departmentId, setDepartmentId] = useState('');
  const [panel, setPanel] = useState<InterviewPanelMember[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');

  const { data: departmentsData } = useGetDepartmentsLookup();
  const departments =
    (departmentsData as { Departments?: { items: DepartmentOption[] } })?.Departments?.items ?? [];

  const { data: employeesData } = useGetEmployeesLookup('');
  const employees =
    (employeesData as { Employees?: EmployeesLookupResult })?.Employees?.items ?? [];

  const { mutateAsync: transferToConditionalOffer, isPending } = useTransferToConditionalOffer();

  if (!profile) return null;

  const addPanelMember = () => {
    const employee = employees.find((option) => option.ItemId === selectedEmployeeId);
    if (!employee || panel.some((member) => member.id === employee.ItemId)) return;
    setPanel([...panel, { name: employee.DisplayName, id: employee.ItemId }]);
    setSelectedEmployeeId('');
  };

  const removePanelMember = (id: string) => {
    setPanel(panel.filter((member) => member.id !== id));
  };

  const handleConfirm = async () => {
    if (!departmentId || panel.length === 0) {
      toast({ title: t('DEPARTMENT_AND_INTERVIEW_PANEL_REQUIRED'), variant: 'destructive' });
      return;
    }
    const departmentName =
      departments.find((department) => department.ItemId === departmentId)?.Title ?? '';
    try {
      await transferToConditionalOffer({
        profile,
        departmentId,
        departmentName,
        interviewPanel: panel,
      });
      setDepartmentId('');
      setPanel([]);
      onOpenChange(false);
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>{t('TRANSFER_TO_CONDITIONAL')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t('DEPARTMENT')}</Label>
            <Select value={departmentId} onValueChange={setDepartmentId}>
              <SelectTrigger>
                <SelectValue placeholder={t('DEPARTMENT')} />
              </SelectTrigger>
              <SelectContent>
                {departments.map((department) => (
                  <SelectItem key={department.ItemId} value={department.ItemId}>
                    {department.Title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1.5">
              <Label>{t('INTERVIEW_PANEL')}</Label>
              <Select value={selectedEmployeeId} onValueChange={setSelectedEmployeeId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('EMPLOYEE')} />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((employee) => (
                    <SelectItem key={employee.ItemId} value={employee.ItemId}>
                      {employee.DisplayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={addPanelMember}
              disabled={!selectedEmployeeId}
            >
              {t('ADD')}
            </Button>
          </div>

          {panel.map((member) => (
            <div
              key={member.id}
              className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
            >
              <span>{member.name}</span>
              <Button variant="ghost" size="icon" onClick={() => removePanelMember(member.id)}>
                <Trash2 className="size-4" />
              </Button>
            </div>
          ))}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <DialogCancelButton onClick={() => onOpenChange(false)}>{t('CANCEL')}</DialogCancelButton>
          <Button type="button" onClick={handleConfirm} loading={isPending} disabled={isPending}>
            {t('TRANSFER')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
